import React, { useMemo } from 'react';
import cx from 'classnames';
import styles from './MutualSettlementsAlert.module.scss';
import { formatAsPrice } from '@/fsd/shared/lib/helpers/formatAsPrice/formatAsPrice';
import Table from '@/fsd/shared/ui/Table';
import HeaderTable from '@/fsd/shared/ui/HeaderTable/HeaderTable';
import HeaderTableItem from '@/fsd/shared/ui/HeaderTableItem';
import BodyTable from '@/fsd/shared/ui/BodyTable';
import BodyTableLine from '@/fsd/shared/ui/BodyTableLine';
import BodyTableLineItem from '@/fsd/shared/ui/BodyTableLineItem';
import EmptyData from '@/fsd/shared/ui/EmptyData/EmptyData';
import { queryClient } from '@/fsd/shared/lib/react-query/queryClient';
import { IApiPaginatedResponse } from '@/fsd/shared/api/types';
import { IReconciliation } from '@/fsd/entities/mutualSettlements';
import { formatDateToDDMMYYYY } from '@/fsd/shared/lib/helpers/formatDateToDDMMYYYY/formatDateToDDMMYYYY';
import StatusBlock from '@/fsd/shared/ui/status-block/status-block';

const MutualSettlementsAlert = ({ id }: { id?: number }) => {
  if (!id) return <EmptyData desc={'ID не выбран'} />;

  const data = useMemo(() => {
    const allQueries = queryClient.getQueriesData<
      IApiPaginatedResponse<IReconciliation[]>
    >({ queryKey: ['mutualSettlements'] });
    for (const [, queryData] of allQueries) {
      if (queryData) {
        const item = queryData.data.find((item) => item.id === id);
        if (item) return item;
      }
    }
    return undefined;
  }, [id]);

  if (!data) return <EmptyData desc={'Данные не найдены'} />;

  const totalDebit = data.rows.reduce(
    (sum, item) => sum + Number(item.debet),
    0
  );
  const totalCredit = data.rows.reduce(
    (sum, item) => sum + Number(item.credit),
    0
  );

  return (
    <div className={styles.container}>
      <div className={styles.title}>Детали взаиморасчетов</div>

      <div className={styles.info}>
        <div className={styles.infoRow}>
          <div className={styles.infoItem}>
            Компания: <span>{data.organisation_name}</span>
          </div>
          <div className={styles.infoItem}>
            Начальное сальдо:{' '}
            <span>{formatAsPrice(Number(data.opening_balance))}</span>
          </div>
        </div>
        <div className={styles.infoRow}>
          <div className={styles.infoItem}>
            Контрагент: <span>{data.counterparty_name}</span>
          </div>
          <div className={styles.infoItem}>
            Конечное сальдо:{' '}
            <span>{formatAsPrice(Number(data.closing_balance))}</span>
          </div>
        </div>
        <div className={styles.infoRow}>
          <div className={styles.infoItem}>
            Период:{' '}
            <span>
              {data.date_from} - {data.date_to}
            </span>
          </div>
          <div className={styles.infoItem}>
            Задолженность в пользу: <span>{data.debt_display}</span>
          </div>
        </div>
      </div>

      <Table className={cx(styles.table)} fixed={true}>
        <HeaderTable className={cx(styles.tableHeader)}>
          <HeaderTableItem name="Дата" className={styles.dateColumn} />
          <HeaderTableItem additionalClass="divider" />
          <HeaderTableItem name="ID" className={styles.idColumn} />
          <HeaderTableItem additionalClass="divider" />
          <HeaderTableItem
            name="Тип операции"
            className={styles.operationTypeColumn}
          />
          <HeaderTableItem additionalClass="divider" />
          <HeaderTableItem name="Дебет" className={styles.debetColumn} />
          <HeaderTableItem additionalClass="divider" />
          <HeaderTableItem name="Кредит" className={styles.creditColumn} />
          <HeaderTableItem additionalClass="divider" />
          <HeaderTableItem name="Статус" className={styles.statusColumn} />
          <HeaderTableItem additionalClass="divider" />
          <HeaderTableItem
            name="Комментарий"
            className={styles.commentColumn}
          />
        </HeaderTable>
        <BodyTable className={cx(styles.tableBody)}>
          {!data.rows.length ? (
            <EmptyData desc="Нет данных" />
          ) : (
            data.rows.map((item) => (
              <BodyTableLine className={cx(styles.tableBodyLine)} key={item.id}>
                <BodyTableLineItem
                  value={item.date}
                  className={styles.dateColumn}
                />
                <BodyTableLineItem additionalClass="empty" />
                <BodyTableLineItem
                  value={item.operation_number}
                  className={styles.idColumn}
                />
                <BodyTableLineItem additionalClass="empty" />
                <BodyTableLineItem
                  value={item.operation_type}
                  className={styles.operationTypeColumn}
                />
                <BodyTableLineItem additionalClass="empty" />
                <BodyTableLineItem
                  value={formatAsPrice(Number(item.debet))}
                  className={styles.debetColumn}
                  additionalClass="alightAmount"
                />
                <BodyTableLineItem additionalClass="empty" />
                <BodyTableLineItem
                  value={formatAsPrice(Number(item.credit))}
                  className={styles.creditColumn}
                  additionalClass="alightAmount"
                />
                <BodyTableLineItem additionalClass="empty" />
                <BodyTableLineItem
                  value={
                    <StatusBlock
                      label={item.document_status || '-'}
                      status={item.document_status ? 'success' : 'error'}
                    />
                  }
                  className={styles.statusColumn}
                />
                <BodyTableLineItem additionalClass="empty" />
                <BodyTableLineItem
                  value={item.comment}
                  className={styles.commentColumn}
                />
              </BodyTableLine>
            ))
          )}
        </BodyTable>
      </Table>

      <div className={styles.footer}>
        <div className={cx(styles.column, styles.label)}>Итого:</div>
        <div className={cx(styles.column)} style={{ width: '150px' }}>
          {/* Date */}
        </div>
        <div className={cx(styles.column)}>{/* ID */}</div>
        <div
          className={cx(styles.column, styles.end, styles.resultValue)}
          style={{ width: '160px' }}
        >
          {formatAsPrice(totalDebit)}
        </div>
        <div
          className={cx(styles.column, styles.end, styles.resultValue)}
          style={{ width: '130px' }}
        >
          {formatAsPrice(totalCredit)}
        </div>
        <div className={cx(styles.column)}>{/* Status & Comment */}</div>
        <div className={cx(styles.column)}>{/* Status & Comment */}</div>
      </div>
    </div>
  );
};

export default MutualSettlementsAlert;
